import { useEffect, type ReactNode } from 'react';
import { StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

import {
  useResearchTutorialActions,
  useResearchTutorialStore,
  useResearchTutorialStoreApi,
} from './researchTutorialContext';

import { SpotlightZone } from '@/components/spotlight/SpotlightTour';
import type { SpotlightPadding } from '@/components/spotlight/spotlightGeometry';
import {
  RESEARCH_TUTORIAL_SEGMENTS,
  zoneKey,
  type ResearchTutorialSegment,
} from '@/constants/researchTutorial';
import { selectIsTutorialLive } from '@/services/stores/researchTutorialStore';

const DEFAULT_HIGHLIGHT_RADIUS = 12;

const HUG_STYLE: ViewStyle = { alignSelf: 'flex-start' };

type ResearchTourZoneProps = {
  segment: ResearchTutorialSegment;
  zone: number;
  /** Whether this instance is the one to highlight, when a surface renders several. */
  active?: boolean;
  /** What the step's action button does, for a step that has one. */
  action?: () => void;
  /** Holds the step's primary button disabled until the reader has done what it asks. */
  canAdvance?: boolean;
  /** Corner radius of the highlight; half the highlight's height draws a circle round a round button. */
  highlightRadius?: number;
  /** Room the highlight leaves around the target on each side. */
  highlightPadding?: SpotlightPadding;
  /**
   * Shrinks the highlight to the target's own width. The zone wraps its target
   * in a view that stretches across its column, so a target narrower than the
   * column, such as a pill, is otherwise highlighted edge to edge.
   */
  hug?: boolean;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
};

/**
 * Marks a surface as one step of the Research tutorial.
 *
 * A zone registers only while a segment is in progress, so once the tutorial is
 * finished or skipped no zone writes to the store again. The wrapper itself
 * always renders, so a reader already typing in the composer keeps focus when
 * a tour starts.
 */
export function ResearchTourZone({
  segment,
  zone,
  active = true,
  action,
  canAdvance = true,
  highlightRadius = DEFAULT_HIGHLIGHT_RADIUS,
  highlightPadding,
  hug = false,
  style,
  children,
}: ResearchTourZoneProps) {
  const store = useResearchTutorialStoreApi();

  const { setZoneAction } = useResearchTutorialActions();

  const isLive = useResearchTutorialStore(selectIsTutorialLive) && active;

  const key = zoneKey(segment, zone);

  useEffect(() => {
    if (!isLive) {
      return;
    }

    return () => store.getState().setZone(key, null);
  }, [isLive, key, store]);

  useEffect(() => {
    if (isLive) {
      store.getState().setZone(key, { canAdvance });
    }
  }, [isLive, key, canAdvance, store]);

  useEffect(() => {
    if (isLive) {
      setZoneAction(key, action);
    }
  });

  return (
    <SpotlightZone
      tourKey={RESEARCH_TUTORIAL_SEGMENTS[segment].tourKey}
      zone={zone}
      active={isLive}
      radius={highlightRadius}
      padding={highlightPadding}
      style={hug ? StyleSheet.flatten([HUG_STYLE, style]) : style}>
      {children}
    </SpotlightZone>
  );
}
